import type { ReactNode } from 'react';

import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/ui/badge';

// Other Meta values, such as NONE and NON_EXISTS, mean the number has no display name review.
const NAME_STATUSES: Record<
  string,
  { flavour: 'successful' | 'warning' | 'destructive'; label: string }
> = {
  APPROVED: { flavour: 'successful', label: 'integrations.whatsapp.display_name.status.approved' },
  AVAILABLE_WITHOUT_REVIEW: {
    flavour: 'successful',
    label: 'integrations.whatsapp.display_name.status.available_without_review',
  },
  PENDING_REVIEW: {
    flavour: 'warning',
    label: 'integrations.whatsapp.display_name.status.pending_review',
  },
  DECLINED: { flavour: 'destructive', label: 'integrations.whatsapp.display_name.status.declined' },
  EXPIRED: { flavour: 'destructive', label: 'integrations.whatsapp.display_name.status.expired' },
};

const THROUGHPUT_LABELS: Record<string, string> = {
  STANDARD: 'integrations.whatsapp.throughput.standard',
  HIGH: 'integrations.whatsapp.throughput.high',
};

export const Detail = ({ label, children }: { label: string; children: ReactNode }) => (
  <div className="flex items-center gap-2">
    <span className="text-sm text-muted-foreground">{label}:</span>
    {children}
  </div>
);

interface MetaLimitDetailsProps {
  messagingLimit?: string;
  nameStatus?: string;
  throughputLevel?: string;
}

export const MetaLimitDetails = ({
  messagingLimit,
  nameStatus,
  throughputLevel,
}: MetaLimitDetailsProps) => {
  const { t } = useTranslation();
  const name = nameStatus ? NAME_STATUSES[nameStatus] : undefined;
  const throughputLabel = throughputLevel ? THROUGHPUT_LABELS[throughputLevel] : undefined;

  return (
    <>
      {messagingLimit?.startsWith('TIER_') && (
        <Detail label={t('integrations.whatsapp.messaging_limit.label')}>
          <p className="font-medium">
            {messagingLimit === 'TIER_UNLIMITED'
              ? t('integrations.whatsapp.messaging_limit.unlimited')
              : t('integrations.whatsapp.messaging_limit.value', {
                  0: messagingLimit.slice('TIER_'.length),
                })}
          </p>
        </Detail>
      )}

      {name && (
        <Detail label={t('integrations.whatsapp.display_name.label')}>
          <Badge type="secondary" flavour={name.flavour}>
            {t(name.label)}
          </Badge>
        </Detail>
      )}

      {throughputLabel && (
        <Detail label={t('integrations.whatsapp.throughput.label')}>
          <p className="font-medium">{t(throughputLabel)}</p>
        </Detail>
      )}
    </>
  );
};
